import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required');
}

const isNative = Capacitor.isNativePlatform();

/**
 * On native (iOS/Android) the WebView's localStorage can be cleared by the OS
 * under storage pressure, which silently logs users out. Capacitor Preferences
 * is backed by native UserDefaults / SharedPreferences and survives that, so we
 * use it as the Supabase auth storage adapter on device. On web we fall back to
 * the default localStorage behaviour.
 */
const nativeStorage = {
  getItem: async (key: string) => {
    const { value } = await Preferences.get({ key });
    return value;
  },
  setItem: async (key: string, value: string) => {
    await Preferences.set({ key, value });
  },
  removeItem: async (key: string) => {
    await Preferences.remove({ key });
  },
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // PKCE is the secure flow for public clients and is required for the
    // deep-link OAuth handoff used by the native apps.
    flowType: 'pkce',
    persistSession: true,
    autoRefreshToken: true,
    // On native we exchange the OAuth code ourselves from the deep link, so
    // Supabase should not try to read it from the initial page URL.
    detectSessionInUrl: !isNative,
    ...(isNative ? { storage: nativeStorage } : {}),
  },
});
